// Client Profitability (requirement 17). Reads approved_invoice_snapshots for
// a period, grouped by client, and reports net service revenue (the real
// margin driver) alongside gross billing (informational, includes
// pass-through). For clients with recorded Direct Cost Control entries (see
// clientDirectCostService.js — lump-sum billers like CBRE with no recovery
// split), that cost is also shown and netted off, since their raw
// netServiceRevenue alone overstates margin.
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { sumRevenueLines } from "./lineCategories";
import { sumGrossBilling } from "./pnlCalculator";
import { getDirectCostVarianceForPeriod } from "./clientDirectCostService";
import { profitabilityFigures } from "./profitabilityMath";
import { activeSnapshotDocs } from "./voidedInvoice";

const SNAPSHOTS = "approved_invoice_snapshots";

export async function getClientProfitabilityForPeriod(accountingPeriod) {
  const [result, directCost] = await Promise.all([
    getDocs(query(collection(db, SNAPSHOTS), where("accountingPeriod", "==", accountingPeriod))),
    getDirectCostVarianceForPeriod(accountingPeriod)
  ]);
  const byClient = new Map();

  activeSnapshotDocs(result).forEach((docSnap) => {
    const data = docSnap.data();
    const clientCode = data.clientCode;
    const lines = data.lines || [];
    const current = byClient.get(clientCode) || {
      clientCode, invoiceCount: 0, netServiceRevenue: 0, grossBilling: 0, grossAmount: 0
    };
    current.invoiceCount += 1;
    current.netServiceRevenue += sumRevenueLines(lines);
    current.grossBilling += sumGrossBilling(lines);
    current.grossAmount += Number(data.totals?.grossAmount || 0);
    byClient.set(clientCode, current);
  });

  return Array.from(byClient.values()).map((row) => {
    const grossBilling = Math.round(row.grossBilling);
    const clientDirectCost = Math.round(directCost.byClient[row.clientCode] || 0);
    const { netServiceRevenue, netOfDirectCost } = profitabilityFigures({
      clientCode: row.clientCode, rawNetServiceRevenue: row.netServiceRevenue, directCost: clientDirectCost
    });
    return {
      ...row,
      netServiceRevenue,
      grossBilling,
      grossAmount: Math.round(row.grossAmount),
      directCost: clientDirectCost,
      netOfDirectCost
    };
  }).sort((a, b) => b.netServiceRevenue - a.netServiceRevenue);
}
